import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Plus, Search, Users, Edit, Trash2, Phone, Mail, MapPin, Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = [
  { value: 'fornitore', label: 'Fornitore' },
  { value: 'cliente', label: 'Cliente' },
  { value: 'subappaltatore', label: 'Subappaltatore' },
  { value: 'consulente', label: 'Consulente' },
];

const emptyForm = {
  name: '', category: 'fornitore', vat_number: '', fiscal_code: '',
  address: '', city: '', province: '', zip_code: '', country: 'Italia',
  phone: '', email: '', pec: '', sdi_code: '', contact_person: '',
  payment_terms: '', bank_iban: '', notes: '',
};

export default function SuppliersPage() {
  const { isRole } = useAuth();
  const canManage = isRole('admin', 'qhse', 'capo_cantiere');
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('__all__');
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterCat !== '__all__') params.category = filterCat;
      if (search.trim()) params.search = search.trim();
      const res = await api.get('/suppliers', { params });
      setSuppliers(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterCat, search]);

  useEffect(() => { load(); setPage(1); }, [load]);

  const openCreate = () => { setEditItem(null); setForm({ ...emptyForm }); setShowDialog(true); };
  const openEdit = (s) => {
    setEditItem(s);
    setForm({ ...emptyForm, ...s });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editItem) {
        await api.put(`/suppliers/${editItem.id}`, form);
      } else {
        await api.post('/suppliers', form);
      }
      setShowDialog(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo fornitore?')) return;
    try { await api.delete(`/suppliers/${id}`); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const paged = suppliers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(suppliers.length / PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Anagrafica Fornitori</h1>
          <p className="text-sm text-slate-500 mt-1">Gestione centralizzata fornitori, clienti e contatti</p>
        </div>
        {canManage && (
          <Button onClick={openCreate} data-testid="add-supplier-btn"><Plus size={16} className="mr-1" /> Nuovo Contatto</Button>
        )}
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {CATEGORIES.map(c => {
          const count = suppliers.filter(s => s.category === c.value).length;
          return (
            <Card key={c.value} className="border-slate-200 shadow-sm" data-testid={`kpi-${c.value}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center">
                  <Users size={18} className="text-slate-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-slate-500">{c.label}</p>
                  <p className="text-xl font-bold text-slate-700">{count}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input className="pl-8" placeholder="Cerca per nome, email, P.IVA..." value={search} onChange={e => setSearch(e.target.value)} data-testid="supplier-search" />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-48" data-testid="supplier-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutte le categorie</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Nome</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>P.IVA</TableHead>
                <TableHead>Citta</TableHead>
                <TableHead>Telefono</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Referente</TableHead>
                {canManage && <TableHead className="w-24">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Nessun risultato</TableCell></TableRow>
              ) : paged.map(s => (
                <TableRow key={s.id} className="hover:bg-slate-50/50" data-testid={`supplier-row-${s.id}`}>
                  <TableCell className="font-medium text-sm">{s.name}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{CATEGORIES.find(c => c.value === s.category)?.label || s.category}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-600">{s.vat_number || '-'}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.city || '-'}{s.province ? ` (${s.province})` : ''}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.phone || '-'}</TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-[180px] truncate">{s.email || '-'}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.contact_person || '-'}</TableCell>
                  {canManage && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`}><Edit size={13} /></Button>
                        {isRole('admin') && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDelete(s.id)} data-testid={`delete-supplier-${s.id}`}><Trash2 size={13} /></Button>}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-1">
          <p className="text-xs text-slate-500">{(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, suppliers.length)} di {suppliers.length}</p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="sup-prev-page"><ChevronLeft size={14} /></Button>
            <span className="text-xs text-slate-600 px-2">Pag. {page}/{totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="sup-next-page"><ChevronRight size={14} /></Button>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Modifica Contatto' : 'Nuovo Contatto'}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5">
                <Label>Nome / Ragione Sociale *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="sup-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="sup-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>P.IVA</Label><Input value={form.vat_number} onChange={e => setForm({ ...form, vat_number: e.target.value })} data-testid="sup-vat-input" /></div>
              <div className="space-y-1.5"><Label>Codice Fiscale</Label><Input value={form.fiscal_code} onChange={e => setForm({ ...form, fiscal_code: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Indirizzo</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Citta</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Provincia</Label><Input value={form.province} onChange={e => setForm({ ...form, province: e.target.value })} maxLength={2} /></div>
              <div className="space-y-1.5"><Label>CAP</Label><Input value={form.zip_code} onChange={e => setForm({ ...form, zip_code: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Telefono</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="sup-email-input" /></div>
              <div className="space-y-1.5"><Label>PEC</Label><Input value={form.pec} onChange={e => setForm({ ...form, pec: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Codice SDI</Label><Input value={form.sdi_code} onChange={e => setForm({ ...form, sdi_code: e.target.value })} maxLength={7} /></div>
              <div className="space-y-1.5"><Label>Referente</Label><Input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Cond. Pagamento</Label><Input value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} /></div>
              <div className="col-span-2 space-y-1.5"><Label>IBAN</Label><Input value={form.bank_iban} onChange={e => setForm({ ...form, bank_iban: e.target.value })} /></div>
              <div className="col-span-2 space-y-1.5"><Label>Note</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={!form.name.trim()} data-testid="sup-save-btn">
              {editItem ? 'Salva Modifiche' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
